import type { FC, ReactNode } from "react";
import { View, Skeleton, SkeletonGroup } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import {
  visibleChannels,
  CATEGORIES,
  CATEGORY_CHANNELS,
  NO_UNAVAILABLE_CHANNELS,
  type NotificationCategory,
  type NotificationChannel,
} from "../NotificationPreferenceGroup/NotificationPreferenceGroup";
import { NOTIFICATION_CHECK_COLUMN_WIDTH } from "../NotificationRowLayout/NotificationRowLayout";

/** Matches `Input`'s medium height, the size `NotificationContactPhoneField` renders at. */
const PHONE_FIELD_HEIGHT = 44;
const CHEVRON_SIZE = 20;
/** `Checkbox`'s default size, and its corner radius at that size. */
const CHECKBOX_SIZE = 20;
const CHECKBOX_RADIUS = CHECKBOX_SIZE * 0.2;

/** Roughly as long as each category's label, so the placeholder rows don't all end at one edge. */
const CATEGORY_LABEL_WIDTHS: Record<NotificationCategory, number> = {
  alarmEvent: 80,
  cameraConfigChange: 130,
};

/** Placeholder sites and cameras: enough to suggest a list without promising how long it is. */
const SITE_KEYS = ["site-1", "site-2"];
const CAMERA_KEYS = ["camera-1", "camera-2", "camera-3"];

export interface NotificationPreferencesFormSkeletonProps {
  /** Pass what the form will get, so the placeholder has as many checkbox columns as the form. */
  readonly hiddenChannels?: ReadonlySet<NotificationChannel> | undefined;
  readonly testID?: string;
}

/**
 * `NotificationPreferencesForm`'s layout with placeholder rows, shown while
 * the preferences first load: the phone number field, then the "All Cameras",
 * "Sites" and "Cameras" sections, each with its two category rows of channel
 * checkboxes. Rows share `NotificationRowLayout`'s column width, so the
 * placeholder checkboxes sit where the real ones will appear.
 */
export const NotificationPreferencesFormSkeleton: FC<NotificationPreferencesFormSkeletonProps> = ({
  hiddenChannels = NO_UNAVAILABLE_CHANNELS,
  testID,
}) => {
  const channels = visibleChannels(hiddenChannels);

  return (
    <SkeletonGroup>
      <View style={styles.container} accessibilityLabel="Loading notification preferences" testID={testID}>
        <View style={styles.phoneField}>
          <Skeleton variant="rounded" height={PHONE_FIELD_HEIGHT} />
          <Skeleton variant="text" width={220} textStyle="xs/regular" style={styles.helperText} />
        </View>
        <View style={styles.divider} />
        <View style={styles.section}>
          <SectionHeaderSkeleton channels={channels} />
          <View style={styles.categoryRows}>
            <CategoryRowsSkeleton channels={channels} />
          </View>
        </View>
        <View style={styles.divider} />
        <ScopeListSkeleton itemKeys={SITE_KEYS} channels={channels} />
        <View style={styles.divider} />
        <ScopeListSkeleton itemKeys={CAMERA_KEYS} channels={channels} />
      </View>
    </SkeletonGroup>
  );
};

/** Stands in for `NotificationScopeListGroup`: a section header, then one block per site or camera. */
const ScopeListSkeleton: FC<{
  readonly itemKeys: readonly string[];
  readonly channels: readonly NotificationChannel[];
}> = ({ itemKeys, channels }) => (
  <View style={styles.section}>
    <SectionHeaderSkeleton channels={channels} />
    <View style={styles.scopeItems}>
      {itemKeys.map((key) => (
        <View key={key} style={styles.section}>
          <Skeleton variant="text" width={110} textStyle="sm/semibold" style={styles.scopeLabel} />
          <View style={styles.categoryRows}>
            <CategoryRowsSkeleton channels={channels} />
          </View>
        </View>
      ))}
    </View>
  </View>
);

/** A collapsible header: chevron, label, and a caption over each channel column. */
const SectionHeaderSkeleton: FC<{ readonly channels: readonly NotificationChannel[] }> = ({ channels }) => (
  <RowSkeleton
    left={
      <>
        <View style={styles.iconColumn}>
          <Skeleton variant="rounded" width={CHEVRON_SIZE} height={CHEVRON_SIZE} />
        </View>
        <Skeleton variant="text" width={90} textStyle="sm/semibold" />
      </>
    }
    columns={channels.map((channel) => (
      <Skeleton key={channel} variant="text" width={28} textStyle="xxs/regular" />
    ))}
  />
);

/** The category rows (Alarm Events, Camera Config Changes) under a scope, with a checkbox only where the form has one. */
const CategoryRowsSkeleton: FC<{ readonly channels: readonly NotificationChannel[] }> = ({ channels }) => (
  <>
    {CATEGORIES.map((category) => (
      <RowSkeleton
        key={category}
        left={<Skeleton variant="text" width={CATEGORY_LABEL_WIDTHS[category]} textStyle="xs/regular" />}
        columns={channels.map((channel) =>
          CATEGORY_CHANNELS[category].has(channel) ?
            <Skeleton
              key={channel}
              variant="rectangular"
              width={CHECKBOX_SIZE}
              height={CHECKBOX_SIZE}
              style={styles.checkbox}
            />
          : null,
        )}
      />
    ))}
  </>
);

/** `NotificationRowLayout`'s shape: a flexing label region, then one fixed-width column per channel. */
const RowSkeleton: FC<{ readonly left: ReactNode; readonly columns: readonly ReactNode[] }> = ({ left, columns }) => (
  <View style={styles.row}>
    <View style={styles.left}>{left}</View>
    <View style={styles.columnsGroup}>
      {columns.map((column, index) => (
        <View key={index} style={styles.checkColumn}>
          {column}
        </View>
      ))}
    </View>
  </View>
);

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.lg,
  },
  phoneField: {
    gap: theme.spacing.xs,
  },
  helperText: {
    marginLeft: theme.spacing.md,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.border,
  },
  section: {
    gap: theme.spacing.md,
  },
  scopeItems: {
    gap: theme.spacing.md,
    paddingLeft: theme.spacing.md,
  },
  scopeLabel: {
    marginLeft: theme.spacing["6xl"],
  },
  categoryRows: {
    gap: theme.spacing.sm,
    paddingLeft: theme.spacing["6xl"],
  },
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing.sm,
    rowGap: theme.spacing.xs,
  },
  left: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    minWidth: 120,
  },
  iconColumn: {
    width: 32,
    alignItems: "center",
  },
  columnsGroup: {
    flexDirection: "row",
    flexWrap: "wrap",
    maxWidth: "100%",
    gap: theme.spacing.sm,
    rowGap: theme.spacing.xs,
    flexShrink: 0,
  },
  checkbox: {
    borderRadius: CHECKBOX_RADIUS,
  },
  checkColumn: {
    width: NOTIFICATION_CHECK_COLUMN_WIDTH,
    alignItems: "center",
    justifyContent: "center",
  },
}));
